<template>
  <div class="day-end-closing-report-container">
    <!-- Enhanced Header with Gradient Background -->
    <div class="day-end-closing-report-header">
      <div class="day-end-closing-header-content">
        <div class="day-end-closing-header-text">
          <h2 class="day-end-closing-report-title">Day End Closing Report</h2>
          <p class="day-end-closing-report-subtitle">Generate comprehensive day-end closing summary</p>
        </div>
      </div>
    </div>

    <!-- Generate Report Section -->
    <div class="day-end-closing-generate-section">
      <b-card class="day-end-closing-generate-card">
        <div class="generate-content">
          <div class="generate-header">
            <h5 class="generate-title">
              <feather-icon icon="FileTextIcon" size="20" class="mr-2" />
              Generate Day End Report
            </h5>
            <p class="generate-description">
              Click the button below to generate the day-end closing report with all sales data and summaries.
            </p>
          </div>
          
          <div class="generate-actions">
            <b-button 
              variant="primary" 
              @click="generateReport" 
              :disabled="isGenerating" 
              class="generate-btn"
              size="lg"
            >
              <feather-icon icon="PlayIcon" size="20" class="mr-2" />
              {{ isGenerating ? 'Generating Report...' : 'Generate Report' }}
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Loading State -->
    <div v-if="isGenerating" class="day-end-closing-loading-container">
      <div class="loading-content">
        <b-spinner type="grow" variant="primary" class="mr-3"></b-spinner>
        <span class="loading-text">Generating day-end closing report...</span>
      </div>
    </div>

   
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import {
  BCard,
  BButton,
  BSpinner,
} from 'bootstrap-vue';

export default {
  name: 'DayEndClosingReport',
  components: {
    ToastificationContent,
    BCard,
    BButton,
    BSpinner,
  },
  data() {
    return {
      isGenerating: false,
    };
  },
  computed: {
    businessID() {
      if (this.$store.state.user && this.$store.state.user.businessID) {
        return this.$store.state.user.businessID;
      }
      if (this.$store.state.businessID) {
        return this.$store.state.businessID;
      }
      if (this.$store.state.userData && this.$store.state.userData.businessID) {
        return this.$store.state.userData.businessID;
      }
      return 1;
    },
    userID() {
      if (this.$store.state.user && this.$store.state.user.userID) {
        return this.$store.state.user.userID;
      }
      if (this.$store.state.userID) {
        return this.$store.state.userID;
      }
      if (this.$store.state.userData && this.$store.state.userData.userID) {
        return this.$store.state.userData.userID;
      }
      return 1;
    },
  },
  methods: {
    generateReport() {
      this.isGenerating = true;
      
      try {
        // Construct the external URL with business ID
        const reportUrl = `https://abdanco.cloudious.net/DayEndClosing/Show?bID=${this.businessID}`;
        
        // Open the external URL in a new tab
        window.open(reportUrl, '_blank');
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Day-end closing report opened in new tab',
          },
        });

      } catch (error) {
        console.error('Error opening report:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Failed to open day-end closing report',
          },
        });
      } finally {
        // Reset the generating state after a short delay
        setTimeout(() => {
          this.isGenerating = false;
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Enhanced Day End Closing Report Styling
.day-end-closing-report-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 1.5rem;
}

// Enhanced Header with Gradient - Day End Closing Report specific
.day-end-closing-report-container .day-end-closing-report-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  
  .day-end-closing-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .day-end-closing-header-text {
      .day-end-closing-report-title {
        color: white;
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      .day-end-closing-report-subtitle {
        color: rgba(255, 255, 255, 0.9);
        font-size: 1.1rem;
        margin-bottom: 0;
        font-weight: 300;
      }
    }
  }
}

// Enhanced Generate Section - Day End Closing Report specific
.day-end-closing-report-container .day-end-closing-generate-section {
  margin-bottom: 2rem;
  
  .day-end-closing-generate-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: none;
    overflow: hidden;
    
    .generate-content {
      padding: 3rem;
      text-align: center;
      
      .generate-header {
        margin-bottom: 2rem;
        
        .generate-title {
          color: #2c3e50;
          margin: 0 0 1rem 0;
          font-weight: 600;
          font-size: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .generate-description {
          color: #6c757d;
          font-size: 1.1rem;
          margin: 0;
          line-height: 1.6;
        }
      }
      
      .generate-actions {
        .generate-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          padding: 1rem 3rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
          }
          
          &:disabled {
            opacity: 0.6;
            transform: none;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}

// Enhanced Loading State - Day End Closing Report specific
.day-end-closing-report-container .day-end-closing-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  
  .loading-content {
    display: flex;
    align-items: center;
    background: white;
    padding: 2rem 3rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    
    .loading-text {
      font-size: 1.1rem;
      color: #6c757d;
      font-weight: 500;
    }
  }
}

</style>

